// apps/ers/src/app/modules/ers-leave-management/components/gift-of-time-page/gift-of-time-page.component.ts
import { Component, OnInit, DestroyRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ApiNotificationService } from '@rcs/notify';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { dateFilterParamsLong,
  filterAndSortOptions,
} from '@rcs/utils';

import { LeaveHolidayPanelComponent } from '../leave-holiday-panel/leave-holiday-panel.component';
import { SuccessDialogComponent } from '../../dialogs/success-dialog/success-dialog';
import { LeaveType, Manager, LeaveBalance } from '../../models/leave.model';
import { LeaveService } from '../../services/leave.service';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { formatDateMDY, getDayName } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSearchableSelectComponent } from '@rcs/ers-elementals';

@Component({
  selector: 'ers-gift-of-time-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDialogModule,
    LeaveHolidayPanelComponent,
    LmLoadingOverlayComponent,
    ErsSearchableSelectComponent
  ],
  templateUrl: './gift-of-time-page.component.html',
  styleUrls: ['./gift-of-time-page.component.scss'],
})
export class GiftOfTimePageComponent implements OnInit {
  // form
  giftForm: FormGroup;

  // chip selection
  selectedRecipients: Manager[] = [];

  // data
  leaveTypes: LeaveType[] = [];
  recipients: Manager[] = [];      // employees/supervisors who can receive
  leaveBalance = 0;                // balance for selected leave type

  // Leave-type autocomplete
  @ViewChild('leaveTypeInput', { read: MatAutocompleteTrigger })
  leaveTypeAutoTrigger!: MatAutocompleteTrigger;

  leaveTypeSearchCtrl = new FormControl('');

  get filteredLeaveTypes(): LeaveType[] {
    return filterAndSortOptions(
      this.leaveTypes,
      this.leaveTypeSearchCtrl.value ?? '',
      (t) => t.name
    );
  }

  // right panel data
  companyHolidayRows: any[] = [];
  optionalHolidayRows: any[] = [];
  pendingWorkedRows: any[] = [];

  holidayColDefs: ColDef[] = [];
  workedDateColDefs: ColDef[] = [];

  isLoading = false;
  isSubmitting = false;

  private destroyRef = inject(DestroyRef);
  private notify = inject(ApiNotificationService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private leaveService: LeaveService,
    private dialog: MatDialog,
  ) {
    this.giftForm = this.fb.group({
      leaveTypeId:          ['', Validators.required],
      days:                 [1, [Validators.required, Validators.min(1)]],
      recipientEmployeeIds: [[], Validators.required],
      reason:               [''],
    });
  }

  ngOnInit(): void {
    this.setupColDefs();
    this.loadData();

    // update balance when leave type changes
    this.giftForm.get('leaveTypeId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshBalance());
  }

  private setupColDefs(): void {
    this.holidayColDefs = [
      {
        headerName: 'Holiday Name',
        field: 'holidayName',
        flex: 1,
        minWidth: 160,
        filter: 'agSetColumnFilter',
        cellRenderer: (p: ICellRendererParams) => {
          const star = p.data?.isMandatory
            ? `<span style="color:#FFC107;margin-right:5px;font-size:14px;">★</span>`
            : `<span style="display:inline-block;width:19px;"></span>`;
          return `<span style="display:flex;align-items:center;">${star}${p.value ?? ''}</span>`;
        },
      },
      { headerName: 'Date', field: 'date', width: 110, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong },
      { headerName: 'Day',  field: 'day',  width: 110, filter: 'agSetColumnFilter' },
      { headerName: 'Type', field: 'type', width: 100, filter: 'agSetColumnFilter' },
    ];
    this.workedDateColDefs = [
      {
        headerName: 'Date of Worked',  field: 'dateWorked',     flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong,
        sort: 'desc', sortIndex: 0,
        comparator: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      },
      { headerName: 'Requested Date',  field: 'requestedDate',  flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong },
      { headerName: 'Status',          field: 'status',         width: 100, filter: 'agSetColumnFilter' },
    ];
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      types:    this.leaveService.getLeaveTypes(),
      managers: this.leaveService.getManagers(),
      holidays: this.leaveService.getHolidays(),
      workedDates: this.leaveService.getWorkedDates(),
      balances: this.leaveService.getMyLeaveBalance(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ types, managers, holidays, workedDates, balances }) => {
        this.isLoading = false;
        this.leaveTypes  = types;
        this.recipients  = managers;

        if (types.length) {
          this.giftForm.patchValue({ leaveTypeId: types[0].id });
          this.leaveTypeSearchCtrl.setValue(types[0].name, { emitEvent: false });
        }

        const rows = holidays.map(h => ({
          holidayName: h.holidayName,
          date:        this.fmt(h.holidayDate),
          day:         getDayName(h.holidayDate),
          type:        h.isOptional ? 'Optional' : 'Mandatory',
          isMandatory: !h.isOptional,
        }));
        this.companyHolidayRows  = rows;
        this.optionalHolidayRows = rows.filter(r => r.type === 'Optional');

        this.pendingWorkedRows = (workedDates ?? []).map((d: any) => ({
          dateWorked: this.fmt(d.date),
          requestedDate: d.requestedDate ? this.fmt(d.requestedDate) : '-',
          status: d.status ?? '-',
          _raw: d,
        }));

        this.refreshBalanceFromList(balances);
      });
  }

  private refreshBalance(): void {
    this.leaveService.getMyLeaveBalance()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(b => this.refreshBalanceFromList(b));
  }

  private refreshBalanceFromList(balances: LeaveBalance[]): void {
    const typeId = this.giftForm.value.leaveTypeId;
    const found  = balances.find(b => b.leaveTypeId === typeId);
    this.leaveBalance = found?.availableBalance ?? 0;
  }

  get selectedLeaveTypeName(): string {
    const id = this.giftForm.value.leaveTypeId;
    return this.leaveTypes.find(t => t.id === id)?.name ?? '';
  }

  // ─── Recipient chips ──────────────────────────────────────────────────────

  onRecipientChange(ids: string[]): void {
    this.selectedRecipients = ids
      .map(id => this.recipients.find(r => r.id === id))
      .filter(Boolean) as Manager[];
  }

  removeRecipient(id: string): void {
    this.selectedRecipients = this.selectedRecipients.filter(r => r.id !== id);
    const ids = this.selectedRecipients.map(r => r.id);
    this.giftForm.patchValue({ recipientEmployeeIds: ids });
  }

  // ─── Leave-type autocomplete ──────────────────────────────────────────────

  onLeaveTypeSelected(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value as string;
    const found = this.leaveTypes.find(t => t.id === id);
    if (found) {
      this.giftForm.patchValue({ leaveTypeId: id });
      this.leaveTypeSearchCtrl.setValue(found.name, { emitEvent: false });
    }
  }

  toggleLeaveTypePanel(event: Event): void {
    event.stopPropagation();
    if (this.leaveTypeAutoTrigger?.panelOpen) {
      this.leaveTypeAutoTrigger.closePanel();
    } else {
      this.leaveTypeAutoTrigger?.openPanel();
    }
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  onReset(): void {
    const firstType = this.leaveTypes[0];
    this.giftForm.reset({
      leaveTypeId:          firstType?.id ?? '',
      days:                 1,
      recipientEmployeeIds: [],
      reason:               '',
    });
    this.leaveTypeSearchCtrl.setValue(firstType?.name ?? '', { emitEvent: false });
    this.selectedRecipients = [];
  }

  onSend(): void {
    if (this.giftForm.invalid) {
      this.giftForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const v = this.giftForm.value;
    this.leaveService.giftLeave({
      leaveTypeId:          v.leaveTypeId,
      recipientEmployeeIds: v.recipientEmployeeIds,
      days:                 v.days,
      reason:               v.reason || undefined,
    }).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res?.success === false || res?.error) {
          this.showError(res?.error ?? 'Failed to gift leave.');
        } else {
          this.showSuccess('Gift Sent!', 'Leave days have been successfully gifted.');
        }
      },
      error: () => { this.isSubmitting = false; },
    });
  }

  private showSuccess(title?: string, desc?: string): void {
    const ref = this.dialog.open(SuccessDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      data: { title, desc },
    });
    ref.afterClosed().subscribe(() => this.navigateBack());
  }

  private showError(message: string): void {
    this.notify.addError(message);
  }

  fmt(d: string | Date): string {
    return formatDateMDY(d);
  }

  navigateBack(): void {
    this.router.navigate(['/ers/leave-management']);
  }
}
